import { DebtAllocationSnapshot, FinancialInputs } from '../types';

export interface AmortizationRow {
  month: number;
  beginningBalance: number;
  payment: number;
  interest: number;
  principal: number;
  endingBalance: number;
}

export interface AmortizationInput {
  principal: number;
  annualRate: number;
  amortYears: number;
  monthlyPayment?: number;
  interestOnly?: boolean;
  interestOnlyMonths?: number;
}

export const calculateMonthlyPayment = (principal: number, annualRate: number, years: number): number => {
  if (annualRate === 0) return principal / Math.max(1, years * 12);
  const r = annualRate / 100 / 12;
  const n = years * 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

export const buildAmortizationScheduleFromInput = (input: AmortizationInput): AmortizationRow[] => {
  const principal = Math.max(0, input.principal);
  const totalMonths = Math.max(1, input.amortYears * 12);
  const monthlyRate = input.annualRate / 100 / 12;
  const amortizingPayment = input.monthlyPayment ?? calculateMonthlyPayment(principal, input.annualRate, input.amortYears);
  const interestOnlyMonths = Math.max(0, input.interestOnlyMonths || 0);

  let balance = principal;
  const schedule = Array.from({ length: totalMonths }, (_, index) => {
    const month = index + 1;
    const beginningBalance = balance;
    const interest = input.annualRate === 0 ? 0 : beginningBalance * monthlyRate;
    const payment = input.interestOnly && month <= interestOnlyMonths
      ? interest
      : amortizingPayment;
    const principalPaid = input.interestOnly && month <= interestOnlyMonths
      ? 0
      : Math.max(0, Math.min(beginningBalance, payment - interest));
    balance = Math.max(0, beginningBalance - principalPaid);

    return {
      month,
      beginningBalance,
      payment,
      interest,
      principal: principalPaid,
      endingBalance: balance
    };
  });

  validateAmortizationSchedule(schedule, principal);
  return schedule;
};

export const buildAmortizationSchedule = (inputs: FinancialInputs): AmortizationRow[] => (
  buildAmortizationScheduleFromInput({
    principal: Math.max(0, inputs.purchasePrice - inputs.downPayment),
    annualRate: inputs.interestRate,
    amortYears: inputs.amortYears
  })
);

export const buildAmortizationScheduleFromDebtSnapshot = (debtSnapshot: DebtAllocationSnapshot): AmortizationRow[] => (
  buildAmortizationScheduleFromInput({
    principal: debtSnapshot.allocatedCurrentBalance,
    annualRate: debtSnapshot.interestRate,
    amortYears: debtSnapshot.amortYears,
    monthlyPayment: debtSnapshot.monthlyPayment,
    interestOnly: debtSnapshot.interestOnly,
    interestOnlyMonths: debtSnapshot.interestOnlyMonths
  })
);

const validateAmortizationSchedule = (schedule: AmortizationRow[], originalPrincipal: number): void => {
  if (!schedule.length) return;

  const firstRow = schedule[0];
  const hasPrincipalPaydown = schedule.some((row) => row.principal > 0);
  const hasDecliningBalance = schedule.some((row, index) => index > 0 && row.endingBalance < schedule[index - 1].endingBalance);
  const finalBalance = schedule[schedule.length - 1].endingBalance;

  if (
    firstRow.beginningBalance !== originalPrincipal ||
    !hasPrincipalPaydown ||
    !hasDecliningBalance ||
    finalBalance < 0
  ) {
    console.warn('Amortization schedule validation failed', {
      originalPrincipal,
      firstRow,
      finalBalance,
      hasPrincipalPaydown,
      hasDecliningBalance
    });
  }
};
